/**
 * RBAC-3.3 · FR-2, FR-2d. Preview and make a duplicate of a role.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. Stripping, who may duplicate, which roles are visible and the
 * name rule are all `20261101a2` (and RBAC-1.1's name guard). What the answers mean, and the
 * acknowledgement that is sent, is `lib/roleDuplication.ts`.
 *
 * Errors are rethrown as `RoleDuplicationError`, carrying the server's code so the dialog can tell
 * a stale acknowledgement (22023, re-preview) from a refusal it should just show.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  DUPLICATE_ROLE_RPC,
  ROLE_DUPLICATION_PREVIEW_RPC,
  duplicateRoleArgs,
  duplicateRoleErrorMessage,
  parseDuplicateResult,
  parseDuplicationPreview,
  type DuplicateRoleResult,
  type RoleDuplicationPreview,
  type RpcErrorLike,
} from '@/lib/roleDuplication';

export class RoleDuplicationError extends Error {
  readonly code: string | null;
  constructor(err: RpcErrorLike | null) {
    super(duplicateRoleErrorMessage(err));
    this.name = 'RoleDuplicationError';
    this.code = err?.code ?? null;
  }
}

/** What a copy of the role would carry, and the reserved permissions it would drop. */
export async function fetchRoleDuplicationPreview(sourceRoleId: number): Promise<RoleDuplicationPreview> {
  const { data, error } = await supabase.rpc(ROLE_DUPLICATION_PREVIEW_RPC, { p_source_role_id: sourceRoleId });
  if (error) throw new RoleDuplicationError(error);
  const preview = parseDuplicationPreview(data);
  if (!preview) throw new RoleDuplicationError({ message: 'The preview came back in a shape this screen does not understand.' });
  return preview;
}

/**
 * Make the copy. `preview` must be the one the admin was shown: its `dropped` list is sent as the
 * acknowledgement, and the server refuses (22023) if that is no longer what it would drop.
 */
export async function duplicateRole(
  preview: RoleDuplicationPreview,
  name: string | null,
  description: string | null,
): Promise<DuplicateRoleResult> {
  const { data, error } = await supabase.rpc(DUPLICATE_ROLE_RPC, duplicateRoleArgs(preview, name, description));
  if (error) throw new RoleDuplicationError(error);
  const result = parseDuplicateResult(data);
  if (!result) throw new RoleDuplicationError({ message: 'The role was duplicated, but the reply was not understood. Reload to see it.' });
  return result;
}
